<script>
  import { onMount } from "svelte";
  import {
    Page,
    Card,
    CardHeader,
    CardContent,
    Block,
    List,
    ListItem,
    ListInput,
    Button,
    Icon,
    f7,
  } from "framework7-svelte";
  import EnvironmentSwitcher from "../components/EnvironmentSwitcher.svelte";
  import TradingNavbar from "../components/TradingNavbar.svelte";
  import { ENGINE_URL, BASE_URL } from "../services/service.config";

  let environment = "LIVE";
  let clientTier = "1-999";
  let side = "SELL"; // "BUY" or "SELL"
  let product = "INTRADAY"; // "INTRADAY" or "DELIVERY"
  let orderType = "LIMIT_ENTRY";
  let scripSymbol = "IDEA";
  let scripCode = "532822";
  let quantity = 1;
  let price = 14.21;
  let isExecuting = false;
  let scripCatalog = [];
  let selectedScripToken = "532822";

  async function loadScripCatalog() {
    try {
      const res = await fetch(`${BASE_URL}/api/v1/master-data/scrips?limit=300`);
      if (res.ok) {
        const json = await res.json();
        scripCatalog = json.data || json.scrips || [];
      }
    } catch (err) {
      console.warn("Could not load scrip catalog for bulk orders:", err);
    }
  }

  onMount(() => {
    loadScripCatalog();
  });

  function handleScripSelect(e) {
    const val = e.target.value;
    selectedScripToken = val;
    const found = scripCatalog.find(
      (s) => String(s.bse_token) === String(val) || String(s.token) === String(val)
    );
    if (found) {
      scripSymbol = (found.symbol || found.instrument || found.name).toUpperCase();
      scripCode = String(found.bse_token || found.token);
    }
  }

  function openStockPicker() {
    const ss = f7.smartSelect.get("#hidden-stock-smart-select");
    if (ss) {
      ss.open();
    }
  }

  function handleEnvChange(newEnv) {
    environment = newEnv;
    sessionStorage.setItem("dealer_terminal_env", newEnv);
  }

  function generateAndDownloadCsv() {
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    const safeSymbol = (scripSymbol || "IDEA").trim().toUpperCase();
    const sideCode = side === "BUY" ? "B" : "S";
    const ordType = orderType === "MARKET_ENTRY" ? "MARKET" : "LIMIT";
    const prodType = product || "INTRADAY";
    
    // Sample tier UCCs matching institutional dealer format (e.g. 1000169007-T5153, 1000134007-T5153)
    const clientUccs = [];
    const prefix = environment === "SIMULATION" ? "SIM" : "1000";
    for (let i = 5153; i <= 5174; i++) {
      clientUccs.push(`${prefix}169007-T${i}`);
      clientUccs.push(`${prefix}134007-T${i}`);
    }

    // Exact 24-column BSE BOLT+ batch upload CSV line format (no header)
    // BSE,Equity,B,IDEA,EQ,,,,,1,14.21,,,0,DAY,Client,1000169007-T5153,,,LIMIT,INTRADAY,,N,
    const rows = clientUccs.map((ucc) =>
      `BSE,Equity,${sideCode},${safeSymbol},EQ,,,,,${safeQty},${price.toFixed(2)},,,0,DAY,Client,${ucc},,,${ordType},${prodType},,N,`
    ).join("\n");

    const blob = new Blob([rows + "\n"], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.className = "external";
    link.setAttribute("href", url);
    link.setAttribute("download", `boltplus_${side.toLowerCase()}_${safeSymbol}_${clientTier}_${Date.now()}.csv`);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);

    f7.toast.create({
      text: `✓ BOLT+ Batch CSV Generated (${clientUccs.length} orders in BSE 24-col format)`,
      closeTimeout: 3000,
    }).open();
  }

  async function executeDirectSimBulk() {
    if (environment !== "SIMULATION") {
      f7.dialog.alert("Direct bulk engine execution is strictly restricted to SIMULATION sandbox mode. For LIVE production, download the BOLT+ CSV.", "Sandbox Protected");
      return;
    }

    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    const safeSymbol = (scripSymbol || "RELIANCE").trim().toUpperCase();
    
    // Generate simulation bulk orders with mandatory SIM_ prefix
    const ordersToSubmit = [
      { ucc: "SIM_001", scrip_code: scripCode, symbol: safeSymbol, side: "BUY", quantity: safeQty, price: Number(price), order_type: "LIMIT" },
      { ucc: "SIM_002", scrip_code: scripCode, symbol: safeSymbol, side: "BUY", quantity: safeQty, price: Number(price), order_type: "LIMIT" },
      { ucc: "SIM_003", scrip_code: scripCode, symbol: safeSymbol, side: "BUY", quantity: safeQty, price: Number(price), order_type: "LIMIT" },
    ];

    // Pre-flight validation: Ensure every UCC has SIM_ prefix
    const invalidUcc = ordersToSubmit.find(o => !o.ucc.startsWith("SIM_"));
    if (invalidUcc) {
      f7.dialog.alert(`Safety Barrier: UCC '${invalidUcc.ucc}' does not have 'SIM_' prefix. Cannot execute in simulation mode.`, "Validation Error");
      return;
    }

    isExecuting = true;
    try {
      const res = await fetch(`${ENGINE_URL}/sim/bulk-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orders: ordersToSubmit }),
      });

      if (!res.ok) {
        throw new Error(`Engine returned HTTP ${res.status}`);
      }

      const data = await res.json();
      f7.toast.create({
        text: `✓ Direct Simulation: ${data.accepted_count || ordersToSubmit.length} orders executed in sandbox!`,
        closeTimeout: 3000,
      }).open();
    } catch (e) {
      f7.dialog.alert(e.message || "Failed to execute bulk simulation orders", "Execution Error");
    } finally {
      isExecuting = false;
    }
  }
</script>

<Page name="dealer-bulk-orders">
  <TradingNavbar title="Dealer Bulk Orders Management">
    <EnvironmentSwitcher
      {environment}
      on:change={(e) => handleEnvChange(e.detail)}
    />
  </TradingNavbar>

  <Block>
    <Card>
      <CardHeader>BOLT+ / ODIN Bulk Upload Generator & Direct Execution</CardHeader>
      <CardContent padding={false}>
        <List>
          <ListItem
            title="Client Group / Tier"
            smartSelect
            smartSelectParams={{ openIn: "popover" }}
          >
            <select name="clientTier" bind:value={clientTier}>
              <option value="1-999">Clients Tier 1-999 (Standard Retail)</option>
              <option value="1000-1999">Clients Tier 1000-1999 (HNI / Pro)</option>
              <option value="2000-9999">Clients Tier 2000+ (Institutional)</option>
            </select>
          </ListItem>

          <ListItem
            title="Order Side"
            smartSelect
            smartSelectParams={{ openIn: "popover" }}
          >
            <select name="side" bind:value={side}>
              <option value="SELL">SELL (Square-off / Short)</option>
              <option value="BUY">BUY (Entry / Long)</option>
            </select>
          </ListItem>

          <ListItem
            title="Product Segment"
            smartSelect
            smartSelectParams={{ openIn: "popover" }}
          >
            <select name="product" bind:value={product}>
              <option value="INTRADAY">INTRADAY (MIS)</option>
              <option value="DELIVERY">DELIVERY (CNC)</option>
            </select>
          </ListItem>

          <ListItem
            title="Order File Type"
            smartSelect
            smartSelectParams={{ openIn: "popover" }}
          >
            <select name="orderType" bind:value={orderType}>
              <option value="TARGET_SL_SQUAREOFF">Target & Stoploss Square-off</option>
              <option value="LIMIT_ENTRY">Multi-Client Limit Entry</option>
              <option value="EOD_FLATTEN">End-of-Day Complete Flatten</option>
            </select>
          </ListItem>

          <!-- Hidden SmartSelect container triggered programmatically -->
          <ListItem
            id="hidden-stock-smart-select"
            class="display-none"
            smartSelect
            smartSelectParams={{
              openIn: "popup",
              searchbar: true,
              searchbarPlaceholder: "Search stock name, symbol (RELIANCE, TCS) or token...",
            }}
          >
            <select name="selectedScripToken" value={selectedScripToken} on:change={handleScripSelect}>
              {#each scripCatalog as scrip}
                <option value={scrip.bse_token || scrip.token}>
                  {scrip.symbol || scrip.instrument || scrip.name} ({scrip.bse_token || scrip.token}) - {scrip.name}
                </option>
              {/each}
            </select>
          </ListItem>

          <ListItem
            title="Stock Symbol"
            after={scripSymbol}
            link="#"
            onClick={openStockPicker}
            footer="Tap to select from master stock catalog"
          />

          <ListItem
            title="BSE Scrip Code"
            after={scripCode}
            link="#"
            onClick={openStockPicker}
            footer="Linked security token from master catalog"
          />

          <ListInput
            label="Limit Price (₹)"
            type="number"
            value={price}
            onInput={(e) => (price = parseFloat(e.target.value))}
          />

          <ListInput
            label="Quantity Per Client"
            type="number"
            value={quantity}
            onInput={(e) => (quantity = parseInt(e.target.value, 10))}
          />
        </List>
      </CardContent>
    </Card>

    <div style="display: flex; gap: 12px; margin-top: 16px;">
      <Button fill large color="blue" onClick={generateAndDownloadCsv} style="flex: 1;">
        <Icon f7="arrow_down_doc_fill" size="18" /> &nbsp; Download BOLT+ Batch CSV
      </Button>
    </div>
  </Block>
</Page>
